/**
 * 🔴 前端上传体积上限 —— **动态取, 绝不写死** (Phase D2-A, v0.18.16)
 *
 * ## 为什么需要这个文件
 *   课评媒体 (LessonReviewModal 的 `<input accept="image/*,video/*" multiple>`) 改造前
 *   **完全没有任何前端体积上限**。那时直传腾讯云 COS, 浏览器 → COS 直连, 本机服务器
 *   不参与, 1GB 视频也没问题。
 *
 *   Phase D2 把上传改走 ziven-core 共享方法后, **本地档是走后端中转的**:
 *     浏览器 → 本机 PHP (upload_max_filesize / post_max_size) → 落盘
 *   于是撞上一个**前端看不见的服务器封顶** —— 生产实测 `upload_max_filesize=2M`。
 *   后果: 前端放行、后端拒, 用户看到一句看不懂的错。
 *
 * ## 上限的三个可能来源 (取最小)
 *   1. `ziven-core.local_max_size` 后台配置的封顶 → 走**签名端点**下发 (`max_bytes`),
 *      **任何登录用户都能读** (CosSignatureController 只挡 guest, 没有 assertAdmin)。
 *   2. 服务器真实封顶 (PHP upload_max_filesize / post_max_size / nginx client_max_body_size)
 *      → 走 `GET /api/ziven/core/storage/max-upload-bytes`。
 *      🔴 **这个端点是管理员权限** (`ServerMaxUploadBytesController::handle()` 里有
 *      `$actor->assertAdmin()`), 教练 / 员工这类**能传课评但不是论坛管理员**的角色
 *      会拿到 403 —— 所以它只是「锦上添花」, 不能作为唯一来源。
 *   3. 兜底常量 `FALLBACK_BYTES` —— 上面两个都拿不到时用, 并且**必须**同时给用户
 *      一句降级提示 (见 `degraded`), 绝不静默放行任意大。
 *
 * ## 🔴 只在本地档才套上限
 *   `provider` 是 cos / qiniu 时是**浏览器直传**, 本机服务器不碰这个包, 套 2MB 上限
 *   会把现在能传的 500MB 课评视频**误杀**。所以本文件先看 provider 再决定要不要上限。
 *
 * ## 缓存
 *   结果缓存 `CACHE_TTL_MS`, 避免每选一次文件就打两个端点。签名本身有效 1 小时,
 *   所以 60 秒 TTL 足够安全, 换存储档后最多 60 秒生效。
 */

import app from 'flarum/forum/app';

/** 管理员专属, 只在当前用户恰好是管理员时才有值 (见文件头「三个来源」第 2 条) */
const MAX_BYTES_ENDPOINT = '/api/ziven/core/storage/max-upload-bytes';

/** 任何登录用户都能读 —— 它顺带告诉我们当前 provider 是哪一档 */
const SIGNATURE_ENDPOINT = '/api/ziven/core/cos/signature';

/** 两个端点都拿不到时的兜底 (生产实测 PHP upload_max_filesize=2M) */
export const FALLBACK_BYTES = 2 * 1024 * 1024;
const CACHE_TTL_MS = 60 * 1000;
let cache = null; // { at, limit }

/** 字节数 → 人话 (2MB / 1.5GB …), 给提示文案用 */
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!isFinite(n) || n <= 0) return '未知';
  if (n >= 1073741824) return (n / 1073741824).toFixed(2) + ' GB';
  if (n >= 1048576) return (n / 1048576).toFixed(2) + ' MB';
  if (n >= 1024) return Math.round(n / 1024) + ' KB';
  return n + ' B';
}

/** 取后端配置里那个 `local_max_size` 封顶; 没配 / 非数字 / 0 都当「不限」(= null) */
function normalizeConfigured(v) {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return isFinite(n) && n > 0 ? n : null;
}

/**
 * 服务器真实封顶 (管理员端点)。非管理员会 403 ⇒ 返回 null, 不抛。
 * `effective_bytes` 为 null 表示「未知」而**不是**「不限」——
 *   nginx 探测不到时真实上限可能比 PHP 两个 ini 值都小 (见 ServerUploadLimits 类注释),
 *   所以宁可当未知, 也不能拿一个可能偏大的数字去放行。
 */
async function fetchServerCap() {
  try {
    const resp = await app.request({
      method: 'GET',
      url: MAX_BYTES_ENDPOINT
    });
    const data = resp && resp.success ? resp.data : null;
    if (!data) return null;
    if (data.effective_unlimited === true) return null;
    return normalizeConfigured(data.effective_bytes);
  } catch (e) {
    // 403 (非管理员) / 端点不存在 / 网络失败 —— 一律当「没有这个信息」
    return null;
  }
}

/** 签名端点: 同时给出 provider 和「后台配的 local_max_size」 */
async function fetchSignatureInfo() {
  try {
    const resp = await app.request({
      method: 'GET',
      url: SIGNATURE_ENDPOINT
    });
    if (!resp || !resp.success || !resp.data) return null;
    const sig = resp.data;
    if (sig.error) return null; // 例如 local 未启用时后端回 {error: '...'}
    return sig;
  } catch (e) {
    return null;
  }
}

/**
 * 解析出「当前这一档, 一个文件最多能传多少字节」。
 *
 * 🔴 刻意**不是** `export async function` —— `flarum-webpack-config` 的 autoExportLoader
 *    用一条正则扫导出名来决定登记哪些名字, `export async` 中间的 async 不在候选里 ⇒
 *    该函数**不会进 registry**。跨扩展 import 走 registry 就会拿到 undefined,
 *    报成毫无线索的 "resolveUploadLimit is not a function"。
 *    (实测踩过: LessonReviewMedia.uploadMedia 至今仍是 `export async function`,
 *    所以它 reg.get() 不到 —— 本仓内部直接 import 不受影响, 但别拿它当范例。)
 *
 * @returns {Promise<{provider: string, bytes: number|null, degraded: boolean}>}
 *   bytes === null  → 这一档前端**不设上限** (云端直传, 或配置明确不限)。
 *                    调用方**不要**拿它当「随便传多大都行」的保证, 只表示前端不拦。
 *   degraded === true → 走的是兜底常量, 提示文案要额外说明「未能确认服务器上限」。
 */
export function resolveUploadLimit() {
  return resolveLimitAsync();
}
async function resolveLimitAsync() {
  if (cache && Date.now() - cache.at < CACHE_TTL_MS) {
    return cache.limit;
  }
  const sig = await fetchSignatureInfo();
  const provider = sig && sig.provider || (sig ? 'cos' : null);

  // ── 情况 A: 连签名都拿不到 (未启用 / 网络坏 / 未登录)
  //    此时 provider 未知, **不猜**。返回 null = 前端不拦, 但标 degraded,
  //    让调用方提示一句「未能确认上传上限」, 而不是假装一切正常。
  if (!sig) {
    cache = {
      at: Date.now(),
      limit: {
        provider: 'unknown',
        bytes: null,
        degraded: true
      }
    };
    return cache.limit;
  }

  // ── 情况 B: 云端直传 —— 本机服务器不参与, 不套本地上限
  if (provider !== 'local') {
    cache = {
      at: Date.now(),
      limit: {
        provider,
        bytes: null,
        degraded: false
      }
    };
    return cache.limit;
  }

  // ── 情况 C: 本地档 —— 上限 = min(后台配的, 服务器真实的), 两个都拿不到才兜底
  const configured = normalizeConfigured(sig.max_bytes);
  const serverCap = await fetchServerCap();
  const candidates = [configured, serverCap].filter(n => n !== null && n > 0);
  if (candidates.length) {
    cache = {
      at: Date.now(),
      limit: {
        provider: 'local',
        bytes: Math.min.apply(null, candidates),
        degraded: serverCap === null
      }
    };
    return cache.limit;
  }

  // 兜底: 服务器封顶完全探不到。仍然**要给上限**, 否则就是「静默放行任意大」。
  cache = {
    at: Date.now(),
    limit: {
      provider: 'local',
      bytes: FALLBACK_BYTES,
      degraded: true
    }
  };
  return cache.limit;
}

/**
 * 超限提示文案 —— 说清「真实上限是多少」以及「为什么是这个数」。
 * @param {number} fileSize 选中的文件字节数
 * @param {{provider: string, bytes: number|null, degraded: boolean}} limit
 */
export function overLimitMessage(fileSize, limit) {
  if (!limit || limit.bytes === null) {
    return '';
  }
  const cap = formatBytes(limit.bytes);
  let msg = `文件太大: ${formatBytes(fileSize)}, 当前上限 ${cap}。请压缩后重试。`;
  if (limit.degraded) {
    msg += ' (未能从服务器读取真实上限, 此为保守估计, 以实际配置为准)';
  }
  if (limit.provider === 'local') {
    msg += ' [当前存储档 = 本地服务器, 文件需经本机中转]';
  }
  return msg;
}

/** 仅供测试: 清掉缓存 */
export function __resetUploadLimitCache() {
  cache = null;
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/uploadLimits', { FALLBACK_BYTES: FALLBACK_BYTES,formatBytes: formatBytes,resolveUploadLimit: resolveUploadLimit,overLimitMessage: overLimitMessage,__resetUploadLimitCache: __resetUploadLimitCache, });